/**
 * Service API pour la récupération des assets
 */

import apiClient from "@/utils/apiClient";

export interface AssetVersionDescriptor {
  fileSize?: number | null;
  createdAt?: string | null;
  storagePath?: string | null;
}

interface GetAssetUrlOptions {
  preferNoDraco?: boolean;
  version?: string;
}

/**
 * Calcule un jeton de version déterministe pour forcer le rechargement
 * d'un asset quand son binaire a été régénéré.
 */
export function getAssetVersionToken(
  asset?: AssetVersionDescriptor | null,
): string | undefined {
  if (!asset) {
    return undefined;
  }

  const parts = [
    typeof asset.fileSize === "number" ? String(asset.fileSize) : null,
    typeof asset.createdAt === "string" && asset.createdAt.length > 0
      ? asset.createdAt
      : null,
    typeof asset.storagePath === "string" && asset.storagePath.length > 0
      ? asset.storagePath
      : null,
  ].filter((value): value is string => value !== null);

  if (parts.length === 0) {
    return undefined;
  }

  return parts.join(":");
}

/**
 * Retourne l'URL publique d'un asset
 */
export function getAssetUrl(
  assetId: string,
  options: GetAssetUrlOptions = {},
): string {
  if (!assetId) {
    throw new Error("ID de l'asset requis");
  }

  const baseUrl = apiClient.defaults.baseURL || "";
  const trimmedBaseUrl = baseUrl.replace(/\/$/, "");
  const assetUrl = `${trimmedBaseUrl}/assets/${encodeURIComponent(assetId)}`;
  const params = new URLSearchParams();

  if (options.preferNoDraco) {
    params.set("preferNoDraco", "1");
  }

  if (options.version) {
    params.set("v", options.version);
  }

  const query = params.toString();
  return query ? `${assetUrl}?${query}` : assetUrl;
}

/**
 * Télécharge un asset et retourne son contenu sous forme de Blob
 */
export async function fetchAsset(assetId: string): Promise<Blob> {
  if (!assetId) {
    throw new Error("ID de l'asset requis");
  }

  const response = await apiClient.get<Blob>(
    `/assets/${encodeURIComponent(assetId)}`,
    {
      responseType: "blob",
    },
  );

  return response.data;
}
